import type { NotebookSharingScope, SharingAudience } from "./notebookSharing"
import type { NotebookSummary as ChatNotebookSummary } from "./notebookSnapshot"
import { formatDate } from "./reportClient"
import type { NotebookSummary, Schedule } from "./reportTypes"

export interface NotebookRow {
  id: string
  title: string
  created_at: string
  created_by: string | null
  kind: "chat" | "generated" | "series"
  seriesStorage?: "chat" | "generated"
  audience?: "private" | "workspace" | "organization" | "public"
  revision?: number
  schedule?: Schedule
  editions?: NotebookSummary[]
  template?: NotebookSummary
  scheduleKnown?: boolean
  scheduleLoading?: boolean
  schedulingAvailable?: boolean
  historyPartial?: boolean
}

export interface GenerativeSchedule extends Schedule {
  template_notebook_id: string
  edition_ids: string[]
}

export interface NotebookCatalog {
  notebooks: NotebookSummary[]
  schedules?: Schedule[]
  generative_schedules?: GenerativeSchedule[]
  scheduling_available?: boolean
}

/** Match only catalog lineage and notebooks visible in the current workspace. */
export function buildChatNotebookRows(
  chats: ChatNotebookSummary[],
  catalog: NotebookCatalog,
  currentUserEmail: string | null = null,
  scheduleLoading = false,
): NotebookRow[] {
  const byId = new Map(chats.map((chat) => [chat.id, chat]))
  const used = new Set<string>()
  const series: NotebookRow[] = []
  for (const schedule of catalog.generative_schedules ?? []) {
    const template = byId.get(schedule.template_notebook_id)
    const editions = schedule.edition_ids
      .map((id) => byId.get(id))
      .filter((item): item is ChatNotebookSummary => item !== undefined)
      .sort((a, b) => Date.parse(b.created_at) - Date.parse(a.created_at))
    if (!template && editions.length === 0) {
      continue
    }
    if (template) {
      used.add(template.id)
    }
    editions.forEach((edition) => used.add(edition.id))
    series.push({
      id: `chat-schedule:${schedule.id}`,
      title: template?.title ?? schedule.name,
      created_at: editions[0]?.created_at ?? template?.created_at ?? "",
      created_by: template?.created_by ?? editions[0]?.created_by ?? null,
      kind: "series",
      seriesStorage: "chat",
      audience:
        schedule.audience ??
        template?.audience ??
        editions[0]?.audience ??
        "private",
      schedule,
      editions,
      template,
      scheduleKnown: true,
      schedulingAvailable: catalog.scheduling_available,
      historyPartial: editions.length !== schedule.edition_ids.length,
    })
  }
  return [
    ...chats
      .filter((chat) => !used.has(chat.id))
      .map(
        (chat): NotebookRow => ({
          ...chat,
          audience: chat.audience ?? "private",
          kind: "chat",
          scheduleLoading,
          // The lineage catalog is owner-scoped; it cannot classify other users' shared notebooks.
          scheduleKnown:
            catalog.generative_schedules !== undefined &&
            Boolean(currentUserEmail) &&
            chat.created_by?.toLowerCase() === currentUserEmail?.toLowerCase(),
        }),
      ),
    ...series,
  ]
}

/** Groups only persisted schedule identities; matching names never establish membership. */
export function buildNotebookSeries(
  catalog: NotebookCatalog,
  creator: string | null,
): NotebookRow[] {
  const schedules = catalog.schedules ?? []
  const ids = new Set(schedules.map((schedule) => schedule.id))
  const templates = new Set(
    schedules.map((schedule) => schedule.template_notebook_id),
  )
  const generated: NotebookRow[] = catalog.notebooks
    .filter(
      (notebook) =>
        !templates.has(notebook.id) && !ids.has(notebook.schedule_id ?? ""),
    )
    .map((notebook) => ({
      ...notebook,
      audience: notebook.audience ?? "private",
      created_by: creator,
      kind: "generated",
      scheduleKnown: catalog.schedules !== undefined,
    }))
  return [
    ...generated,
    ...schedules.map((schedule): NotebookRow => {
      const editions = catalog.notebooks
        .filter((notebook) => notebook.schedule_id === schedule.id)
        .sort(
          (a, b) =>
            Date.parse(b.period?.end ?? b.created_at) -
            Date.parse(a.period?.end ?? a.created_at),
        )
      const template = catalog.notebooks.find(
        (notebook) => notebook.id === schedule.template_notebook_id,
      )
      const latestSaved = editions.reduce<string | undefined>(
        (latest, edition) =>
          latest === undefined ||
          Date.parse(edition.created_at) > Date.parse(latest)
            ? edition.created_at
            : latest,
        undefined,
      )
      return {
        id: `schedule:${schedule.id}`,
        title: template?.title ?? schedule.name,
        created_at:
          latestSaved ??
          template?.created_at ??
          schedule.created_at ??
          schedule.updated_at ??
          "",
        created_by: creator,
        kind: "series",
        audience:
          schedule.audience ??
          template?.audience ??
          editions[0]?.audience ??
          "private",
        schedule,
        editions,
        template,
        scheduleKnown: true,
        schedulingAvailable: catalog.scheduling_available,
      }
    }),
  ]
}

export function notebookVisibilityLabel(row: NotebookRow): string {
  const labels: Record<NonNullable<NotebookRow["audience"]>, string> = {
    private: "Only me",
    workspace: "This workspace",
    organization: "This organization",
    public: "Anyone with the link",
  }
  return labels[row.audience ?? "private"]
}

export function notebookSharingScopeForRow(
  row: NotebookRow,
  organizationSlug: string,
  workspaceId: string,
): NotebookSharingScope {
  if (row.kind === "series") {
    return {
      notebookId: row.id,
      organizationSlug,
      workspaceId,
      targetKind:
        row.seriesStorage === "chat" ? "chat-series" : "generated-series",
      shareableNotebookId:
        row.seriesStorage === "chat"
          ? (row.template?.id ?? row.editions?.[0]?.id ?? null)
          : null,
    }
  }
  return {
    notebookId: row.id,
    organizationSlug,
    workspaceId,
    targetKind: row.kind === "generated" ? "generated" : "chat",
  }
}

export function applyRowAudienceToChats(
  chats: ChatNotebookSummary[] | null,
  row: NotebookRow,
  audience: SharingAudience,
): ChatNotebookSummary[] | null {
  if (!chats) {
    return chats
  }
  if (row.kind === "chat") {
    return chats.map((item) =>
      item.id === row.id ? { ...item, audience } : item,
    )
  }
  if (row.kind === "series" && row.seriesStorage === "chat") {
    const ids = new Set<string>([
      ...(row.template ? [row.template.id] : []),
      ...(row.editions?.map((edition) => edition.id) ?? []),
    ])
    return chats.map((item) =>
      ids.has(item.id) ? { ...item, audience } : item,
    )
  }
  return chats
}

export function applyRenamedGeneratedNotebook(
  catalog: NotebookCatalog,
  notebookId: string,
  renamed: { title: string; revision?: number },
): NotebookCatalog {
  return {
    ...catalog,
    notebooks: catalog.notebooks.map((item) =>
      item.id === notebookId && (item.revision ?? 0) <= (renamed.revision ?? 0)
        ? { ...item, title: renamed.title, revision: renamed.revision }
        : item,
    ),
  }
}

export function applyRowAudienceToCatalog(
  catalog: NotebookCatalog,
  row: NotebookRow,
  audience: SharingAudience,
): NotebookCatalog {
  if (row.kind === "generated") {
    return {
      ...catalog,
      notebooks: catalog.notebooks.map((item) =>
        item.id === row.id ? { ...item, audience } : item,
      ),
    }
  }
  if (row.kind === "series" && row.seriesStorage === "chat") {
    const scheduleId = row.schedule?.id ?? row.id.replace(/^chat-schedule:/, "")
    return {
      ...catalog,
      ...(catalog.generative_schedules
        ? {
            generative_schedules: catalog.generative_schedules.map((item) =>
              item.id === scheduleId ? { ...item, audience } : item,
            ),
          }
        : {}),
    }
  }
  if (row.kind === "series") {
    const scheduleId = row.schedule?.id ?? row.id.replace(/^schedule:/, "")
    const ids = new Set<string>([
      ...(row.template ? [row.template.id] : []),
      ...(row.editions?.map((edition) => edition.id) ?? []),
    ])
    return {
      ...catalog,
      notebooks: catalog.notebooks.map((item) =>
        ids.has(item.id) || item.schedule_id === scheduleId
          ? { ...item, audience }
          : item,
      ),
      ...(catalog.schedules
        ? {
            schedules: catalog.schedules.map((item) =>
              item.id === scheduleId ? { ...item, audience } : item,
            ),
          }
        : {}),
    }
  }
  return catalog
}

export function applyScheduleSlackChannelsToCatalog(
  catalog: NotebookCatalog,
  scheduleId: string,
  slackChannelIds: string[] | null,
): NotebookCatalog {
  const update = <T extends { id: string; slack_channel_ids?: string[] }>(
    item: T,
  ): T => {
    if (item.id !== scheduleId) {
      return item
    }
    const next = { ...item }
    if (slackChannelIds === null) {
      delete next.slack_channel_ids
    } else {
      next.slack_channel_ids = slackChannelIds
    }
    return next
  }
  return {
    ...catalog,
    ...(catalog.schedules ? { schedules: catalog.schedules.map(update) } : {}),
    ...(catalog.generative_schedules
      ? { generative_schedules: catalog.generative_schedules.map(update) }
      : {}),
  }
}

export function notebookScheduleLabel(row: NotebookRow): string {
  if (!row.schedule) {
    return row.scheduleKnown
      ? "One-off"
      : row.scheduleLoading
        ? "Checking schedule…"
        : "Schedule unavailable"
  }
  const schedule = row.schedule
  if (
    schedule.execution_mode === "manual" ||
    schedule.status === "manual_only"
  ) {
    return "Manual recipe"
  }
  const cadence =
    { daily: "Daily", weekly: "Weekly", monthly: "Monthly" }[
      schedule.cadence
    ] ?? schedule.cadence
  if (!schedule.enabled) {
    return `${cadence} · Paused`
  }
  if (row.schedulingAvailable === false) {
    return `${cadence} · Unavailable`
  }
  return cadence
}

/** Reporting periods end at the following midnight; show the last included calendar day. */
export function notebookPeriodLabel(period: {
  start: string
  end: string
}): string {
  const start = period.start.slice(0, 10)
  const end = new Date(`${period.end.slice(0, 10)}T12:00:00Z`)
  if (Number.isNaN(end.getTime())) {
    return `${formatDate(start)} – ${formatDate(period.end)}`
  }
  end.setUTCDate(end.getUTCDate() - 1)
  const lastDay = end.toISOString().slice(0, 10)
  return start === lastDay
    ? formatDate(start)
    : `${formatDate(start)} – ${formatDate(lastDay)}`
}
